import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, X, Check } from "lucide-react";
import { Button } from "./ui/button";
import { api, errMsg } from "../lib/api";

function MemberLimit({ u, reload }) {
  const [lead, setLead] = useState(u.lead_status?.allowance ?? "");
  const [text, setText] = useState(u.text_effective ?? "");
  const dirty = String(lead) !== String(u.lead_status?.allowance ?? "") || String(text) !== String(u.text_effective ?? "");
  const save = async () => {
    try {
      const v = (x) => (x === "" ? null : Number(x));
      if (String(lead) !== String(u.lead_status?.allowance ?? "")) await api.patch("/limits/users", { ids: [u.id], key: "lead_daily", value: v(lead), effective: "now", reason: "Set on team card" });
      if (String(text) !== String(u.text_effective ?? "")) await api.patch("/limits/users", { ids: [u.id], key: "text_daily", value: v(text), effective: "now", reason: "Set on team card" });
      toast.success(`${u.display_name}'s limits saved`); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-1">
      <input type="number" min={0} className="fld w-20 h-8" value={lead} onChange={(e) => setLead(e.target.value)} title="Leads per day" placeholder="leads" data-testid={`member-lead-limit-${u.id}`} />
      <input type="number" min={0} className="fld w-20 h-8" value={text} onChange={(e) => setText(e.target.value)} title="Texts per day" placeholder="texts" data-testid={`member-text-limit-${u.id}`} />
      <Button size="sm" variant={dirty ? "default" : "ghost"} disabled={!dirty} onClick={save} data-testid={`member-limit-save-${u.id}`}><Check className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

export default function TeamMembers({ team, users, reload }) {
  const [pick, setPick] = useState("");
  const [nu, setNu] = useState({ display_name: "", email: "" });
  const members = users.filter((u) => u.team_id === team.id);
  const others = users.filter((u) => u.team_id !== team.id && !u.is_owner);
  const leadsTeam = (u) => u.role === "leader" && u.team_id && u.team_id !== team.id;
  const why = (u) => {
    if (!u.access_enabled) return "no access";
    if (u.paused) return "paused";
    if (!u.request_eligible) return "not eligible";
    if (u.role === "leader" && !u.personal_leads) return "leader (personal leads off)";
    if (u.lead_status?.allowance == null) return "allowance not set";
    if (u.lead_status.allowance === 0) return "allowance disabled";
    return null;
  };
  const move = async (uid, team_id, reason) => {
    try {
      await api.patch("/users/bulk", { user_ids: [uid], changes: { team_id, ...(team_id ? { role: users.find((u) => u.id === uid)?.role === "none" ? "rep" : undefined } : {}) }, reason });
      toast.success(team_id ? "Added to team" : "Removed from team");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const add = async () => {
    const u = users.find((x) => x.id === pick);
    if (u?.team_id && !window.confirm(`${u.display_name} is on another team. Move them here? Their usage today is kept.`)) return;
    await move(pick, team.id, "Added via team page");
    setPick("");
  };
  const create = async () => {
    try {
      await api.post("/users", { display_name: nu.display_name, email: nu.email || null, role: "rep", team_id: team.id });
      setNu({ display_name: "", email: "" }); toast.success("Rep created on this team"); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 text-xs" data-testid={`team-members-${team.id}`}>
      <div className="font-semibold text-sm">{members.length} member{members.length === 1 ? "" : "s"}</div>
      <div className="max-h-80 overflow-auto border rounded">
        <table className="dt"><thead><tr><th>Member</th><th>Role</th><th>Leads / texts per day</th><th>Team total</th><th>Email</th><th></th></tr></thead><tbody>{members.map((u) => (
          <tr key={u.id} data-testid={`team-member-${team.id}-${u.id}`}>
            <td className="font-medium">{u.display_name}</td><td>{u.id === team.leader_user_id ? "leader" : u.role}</td>
            <td><MemberLimit key={`${u.lead_status?.allowance}-${u.text_effective}`} u={u} reload={reload} /></td>
            <td className={`whitespace-nowrap ${why(u) ? "text-amber-700" : "text-emerald-700"}`} title={why(u) ? `Not counted: ${why(u)}` : "Counts toward team"} data-testid={`team-member-counts-${team.id}-${u.id}`}>{why(u) ? `No: ${why(u)}` : "Counted"}</td>
            <td className="mono">{u.email || "no email"}</td>
            <td className="text-right">{u.id !== team.leader_user_id && (
              <Button size="sm" variant="ghost" onClick={() => move(u.id, null, "Removed via team page")} data-testid={`team-member-remove-${team.id}-${u.id}`}><X className="w-3.5 h-3.5" /></Button>)}</td>
          </tr>))}</tbody></table>
      </div>
      <div className="flex flex-wrap gap-2">
        <select className="fld flex-1 min-w-[180px]" value={pick} onChange={(e) => setPick(e.target.value)} data-testid={`team-add-select-${team.id}`}>
          <option value="">Add existing person…</option>
          {others.map((u) => <option key={u.id} value={u.id} disabled={leadsTeam(u)}>{u.display_name}{leadsTeam(u) ? " (leads another team)" : u.team_id ? " (on another team)" : ""}</option>)}
        </select>
        <Button size="sm" disabled={!pick} onClick={add} data-testid={`team-add-btn-${team.id}`}><UserPlus className="w-3.5 h-3.5 mr-1" />Add</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <input className="fld flex-1 min-w-[140px]" placeholder="New rep name" value={nu.display_name} onChange={(e) => setNu({ ...nu, display_name: e.target.value })} data-testid={`team-new-rep-name-${team.id}`} />
        <input className="fld flex-1 min-w-[160px]" placeholder="Google login email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} data-testid={`team-new-rep-email-${team.id}`} />
        <Button size="sm" variant="outline" disabled={!nu.display_name} onClick={create} data-testid={`team-new-rep-btn-${team.id}`}>Create rep</Button>
      </div>
      <div className="text-zinc-500">Set each member's daily leads and texts right here (applies now). Every counted member's leads add up to the team's daily total.</div>
    </div>
  );
}
